// Business Unit detail page for the Assignment Completion report (ADR-094)
//
// The people behind one BU row: per-individual Expected / Open /
// Completed / Missing / %, their Representative, and an expandable list of
// their actual assignments (source, Sequential/Parallel, status).
//
// The server builds this from the SAME population as the row, so the header
// totals here restate the number the user clicked rather than recomputing it.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import useOpenIndividual from '../../../../hooks/useOpenIndividual';
import { INDIVIDUAL_ENTRY_POINTS } from '../../../../constants/individualOpenModes';
import { FaArrowLeft, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import {
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, MainSection, BackButton } from '../../styles';
import {
  FiltersBar,
  HeaderStats,
  HeaderStat,
  GridHeader,
  IndividualRow,
  AssignmentList,
  AssignmentLine,
} from './styles';
import { AssignmentFrozenBadge } from '../../../common/AssignmentFrozenBadge';
import { EmployeeAvatar } from '../../../common/EmployeeAvatar';
import LoadingSpinner from '../../../common/LoadingSpinner';
import EmptyState from '../../../common/EmptyState';
import ResultsCount from '../../../common/ResultsCount';
import { assignmentCompletionApi } from '../../../../services/api/assignmentCompletionApi';
import { useAbility } from '../../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../../constants/permissions';
import {
  getColumnDescriptions,
  describeSkippedBreakdown,
  getSkippedReasonLabel,
  SKIPPED_REASONS,
} from '../../../../constants/assignmentCompletion';
import {
  getStatusText,
  getStatusColor,
  getStatusTextColor,
  getStatusDescription,
} from '../../../../constants/assignmentStatuses';
// Sequential/Parallel already have a palette in this system — blue and green,
// used by the session Type selection. Reuse it rather than inventing a grey.
import {
  getReviewTypeColor,
  getReviewTypeBorderColor,
  getReviewTypeTextColor,
  getReviewTypeText,
} from '../../../../constants/reviewTypes';
import { formatDateOnly } from '../../../../utils/dateOnly';

const REPORT_PATH = '/reports/assignment-completion';

// The three states worth chasing, and they partition: someone with nothing
// created has no unfinished work, and neither is "all done".
const ATTENTION = {
  NOTHING_CREATED: 'nothing-created',
  UNFINISHED: 'unfinished',
  COMPLETE: 'complete',
};

const ATTENTION_OPTIONS = [
  { value: '', label: 'Everyone' },
  { value: ATTENTION.NOTHING_CREATED, label: 'Nothing created' },
  { value: ATTENTION.UNFINISHED, label: 'Has unfinished work' },
  { value: ATTENTION.COMPLETE, label: 'Fully complete' },
];

// Which population to list. Active is the default because it is the one the
// report's numbers are about; skipped people are shown on request, with every
// metric blank.
const POPULATION = {
  ACTIVE: 'active',
  SKIPPED: 'skipped',
  ALL: 'all',
};

const POPULATION_OPTIONS = [
  { value: POPULATION.ACTIVE, label: 'Active' },
  { value: POPULATION.SKIPPED, label: 'Skipped' },
  { value: POPULATION.ALL, label: 'All' },
];

// ADR-108: WHY a person is skipped — a facet beside the selects, shown only
// while skipped people are on screen. Never a fourth `Show` value: Show
// partitions the population, this narrows one partition (ADR-014 §10m).
const SKIP_REASON_OPTIONS = [
  { value: '', label: 'All reasons' },
  { value: SKIPPED_REASONS.NEW_JOINER, label: 'New joiners' },
  { value: SKIPPED_REASONS.ENGAGEMENT_MODEL, label: 'Excluded engagement models' },
];

// ADR-197: date-only values — the stored calendar day, never a local instant
const formatDate = (value) => formatDateOnly(value);

/** "from 01/02/2026" / "01/02/2026 – 30/06/2026" — why this person sits here. */
const formatAllocationWindow = ({ allocationStartDate, allocationEndDate }) => {
  if (!allocationStartDate) {
    return 'on this project';
  }
  return allocationEndDate
    ? `${formatDate(allocationStartDate)} – ${formatDate(allocationEndDate)}`
    : `from ${formatDate(allocationStartDate)}`;
};

/** "by position" / "by role type" / nothing for the cluster default. */
const expectationTag = (source) => {
  if (source?.type === 'position') return 'by position';
  if (source?.type === 'orgUnitType') return 'by role type';
  return null;
};

const expectationTooltip = (source) => {
  if (source?.type === 'position') {
    return `${source.label || 'Their position'} has its own expectation in this cluster, overriding both the general rule and any rule for its type.`;
  }
  if (source?.type === 'orgUnitType') {
    return `${source.label || 'Their position type'} positions have their own expectation in this cluster, overriding the general rule.`;
  }
  return '';
};

const ServiceUnitDetail = () => {
  const navigate = useNavigate();
  const openIndividual = useOpenIndividual();
  const location = useLocation();
  const { businessUnitId } = useParams();
  const ability = useAbility();
  const canReadFeedbackSessions = ability.can(ACTIONS.READ, SUBJECTS.FEEDBACK_SESSION);

  // The report's own filter state travels with the navigation, so Back
  // restores the report exactly as it was left (ADR-090 contract).
  const acrState = location.state?.acrState || {};

  // THIS page's own view state, when we are being returned to rather than
  // opened fresh (ADR-094 amendment). acrState only ever described the report
  // behind this page; leaving for a session and coming back still reset the
  // filters, the expanded rows and the scroll position, so a reader who
  // opened one assignment out of thirty-one landed back at the top of an
  // unfiltered list and had to reconstruct where they were.
  //
  // Read from ONE place on purpose: FeedbackSessionReport returns via
  // `navigate(returnTo, { state: returnState })` and EmployeeForm via
  // `navigate(returnTo, { state: location.state })`, and both land this object
  // at the top level — so the restore path does not branch on who sent us.
  const restoredView = location.state?.detailState;
  const restoredScrollY = restoredView?.scrollY;

  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  // Serialized as an array in history state; a Set is what the page works in.
  const [expandedIds, setExpandedIds] = useState(() => new Set(restoredView?.expandedIds || []));

  // Filters are PURE CLIENT-SIDE: one BU's population arrives in full, so
  // narrowing needs no round trip and cannot drift from the population the
  // row counted. Same approach as the Feedback Session Report.
  const [population, setPopulation] = useState(restoredView?.population || POPULATION.ACTIVE);
  const [skipReason, setSkipReason] = useState(restoredView?.skipReason || '');
  const [attention, setAttention] = useState(restoredView?.attention || '');
  const [representativeId, setRepresentativeId] = useState(restoredView?.representativeId || '');
  const [projectId, setProjectId] = useState(restoredView?.projectId || '');

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setIsLoading(true);
        setError(false);
        const data = await assignmentCompletionApi.getServiceUnitIndividuals(businessUnitId, {
          year: acrState.year,
          assignmentKind: acrState.assignmentKind,
          representativeId: acrState.representative?.id,
          reportingScope: acrState.representative ? acrState.representativeScope : undefined,
        });
        setDetail(data);
      } catch (err) {
        console.error('Error fetching business unit individuals:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetail();
    // acrState comes from navigation state and is stable for the page's life
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId]);

  const toggleExpanded = useCallback((employeeId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  }, []);

  // THE WINDOW DOES NOT SCROLL on this page. The shared report shell pins
  // Container to 100vh with Content overflow:hidden, which makes MainSection
  // the scrolling element — so window.scrollY reads 0 no matter where the
  // reader is, and window.scrollTo() moves nothing. Both halves of the
  // restore have to address the same element, and it is this one.
  const scrollAreaRef = useRef(null);
  const readScrollTop = () => scrollAreaRef.current?.scrollTop || 0;

  // Restore once, after the rows exist to scroll within: while isLoading the
  // section holds only a spinner, has no scrollable height, and the assignment
  // is silently discarded. rAF waits for the rows to paint.
  const scrollRestored = useRef(false);
  useEffect(() => {
    if (isLoading || scrollRestored.current) {
      return;
    }
    scrollRestored.current = true;
    if (typeof restoredScrollY === 'number' && restoredScrollY > 0) {
      requestAnimationFrame(() => {
        if (scrollAreaRef.current) {
          scrollAreaRef.current.scrollTop = restoredScrollY;
        }
      });
    }
  }, [isLoading, restoredScrollY]);

  // Everything a reader would have to redo by hand if we dropped it: the four
  // filters, which rows they had opened, and where they were on the page.
  // Sent with every outward link, so the return trip is exact rather than
  // approximate. expandedIds becomes an array — history state survives a
  // reload, and a Set does not round-trip through it dependably.
  const buildDetailState = () => ({
    population,
    skipReason,
    attention,
    representativeId,
    projectId,
    expandedIds: [...expandedIds],
    scrollY: readScrollTop(),
  });

  const backToReport = () => navigate(REPORT_PATH, { state: { acrState } });

  // Where an assignment line goes when clicked (ADR-094 amendment). The drill
  // path org → BU → person ended here holding a session NAME, which had to be
  // copied and hunted for; this is the hop where someone actually acts on
  // "Left Incomplete".
  //
  // Session-attached work opens the session's own report — the question at
  // this point is usually "is this session stalled for everyone, or just this
  // person?". An individual assignment has no session, so it opens its own
  // report instead; both live behind the SAME permission, so no line is left
  // dead for a reader who can follow the others.
  const assignmentTarget = (assignment) => {
    // Gated separately from this report: the session routes require
    // read:FeedbackSession while this page requires
    // read:AssignmentCompletionReport, and BU managers are deliberately
    // granted the latter without the former. Rendering an unfollowable link
    // would dead-end exactly the audience the manager card was built for, so
    // without the ability the name stays plain text.
    if (!canReadFeedbackSessions) {
      return null;
    }
    if (assignment.source?.type === 'session' && assignment.source.id) {
      return `/feedback-sessions/report/${assignment.source.id}`;
    }
    if (assignment.source?.type === 'individual' && assignment.id) {
      return `/feedback-sessions/individual-report/${assignment.id}`;
    }
    return null;
  };

  // Back from the session report comes home to THIS business unit with the
  // report's filters intact. Uses the app's returnTo / returnLabel /
  // returnState convention (CVRepository, EmployeesRepository): returnState is
  // handed to the destination verbatim, so the acrState that travels
  // report → BU detail keeps travelling rather than being rebuilt.
  const openAssignmentSource = (path) => {
    navigate(path, {
      state: {
        returnTo: `${REPORT_PATH}/service-units/${businessUnitId}`,
        returnLabel: detail?.businessUnit?.name || 'Business Unit',
        returnState: { acrState, detailState: buildDetailState() },
      },
    });
  };

  const handleNavigateToEmployee = (individual, event) => {
    // Don't also toggle the row when following the person link
    event.stopPropagation();
    // ADR-109 — auto open (Edit when the user manages the individual)
    openIndividual(individual.employee.id, {
      entryPoint: INDIVIDUAL_ENTRY_POINTS.ASSIGNMENT_COMPLETION_SERVICE_UNIT,
      returnTo: `${REPORT_PATH}/service-units/${businessUnitId}`,
      returnLabel: detail?.businessUnit?.name || 'Business Unit',
      state: {
        acrState,
        // EmployeeForm returns via navigate(returnTo, { state: location.state }),
        // so this rides along and lands top-level, same as the session route's
        // returnState — the person link restores the view too, not just the
        // session link.
        detailState: buildDetailState(),
      },
    });
  };

  const summary = detail?.summary || {
    eligibleCount: 0,
    expectedCount: 0,
    completedCount: 0,
    notCompletedCount: 0,
    missingCount: 0,
    skippedCount: 0,
    skippedBreakdown: null,
    completionRate: null,
  };
  const individuals = useMemo(() => detail?.individuals || [], [detail]);
  const skippedIndividuals = useMemo(() => detail?.skippedIndividuals || [], [detail]);
  // ADR-108: the year's excluded models ride with the response so the copy
  // here matches the report's
  const columnHelp = getColumnDescriptions({ excludedEngagementModels: detail?.excludedEngagementModels });
  const skippedHeaderBreakdown = describeSkippedBreakdown(summary.skippedCount, summary.skippedBreakdown);
  const skippedHeaderHelp = skippedHeaderBreakdown
    ? `${skippedHeaderBreakdown}. ${columnHelp.skipped}`
    : columnHelp.skipped;
  const skipTagHelp = (person) => (person.skipReason === SKIPPED_REASONS.ENGAGEMENT_MODEL
    ? `Not measured: ${person.engagementModel?.name || 'this engagement model'} is excluded from Assignment Completion for ${detail?.period?.year || 'this year'}. No expectation is set and this person's assignments are not counted either way.`
    : columnHelp.skipped);

  // The rows the population filter puts in play, before the other filters.
  const populationRows = useMemo(() => {
    if (population === POPULATION.SKIPPED) {
      return skippedIndividuals;
    }
    if (population === POPULATION.ALL) {
      return [...individuals, ...skippedIndividuals];
    }
    return individuals;
  }, [population, individuals, skippedIndividuals]);

  // Filter option lists are built from the loaded rows, so they only ever
  // offer values that exist in this BU.
  const representativeOptions = useMemo(() => {
    const byId = new Map();
    for (const individual of populationRows) {
      const manager = individual.directManager;
      if (manager?.id && !byId.has(manager.id)) {
        byId.set(manager.id, manager.fullName || 'Unnamed');
      }
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [populationRows]);

  const projectOptions = useMemo(() => {
    const byId = new Map();
    for (const individual of populationRows) {
      if (individual.project?.id && !byId.has(individual.project.id)) {
        byId.set(individual.project.id, individual.project.name || 'Unnamed');
      }
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [populationRows]);

  const visibleIndividuals = useMemo(() => populationRows.filter((individual) => {
    // ATTENTION only applies to the measured population — a skipped person
    // has no assignment state to be in, so filtering them by one would be
    // answering a question that was never asked of them.
    if (attention && !individual.skipped) {
      if (attention === ATTENTION.NOTHING_CREATED && individual.assignments.length > 0) {
        return false;
      }
      if (attention === ATTENTION.UNFINISHED && individual.notCompletedCount === 0) {
        return false;
      }
      if (attention === ATTENTION.COMPLETE
        && !(individual.notCompletedCount === 0 && individual.missingCount === 0
          && individual.completedCount > 0)) {
        return false;
      }
    }
    // ADR-108: the reason facet applies only to skipped rows — a measured
    // person has no skip reason to match, and hiding them would make "All"
    // lie about what it shows.
    if (skipReason && individual.skipped
      && (individual.skipReason || SKIPPED_REASONS.NEW_JOINER) !== skipReason) {
      return false;
    }
    if (representativeId && individual.directManager?.id !== representativeId) {
      return false;
    }
    if (projectId && individual.project?.id !== projectId) {
      return false;
    }
    return true;
  }), [populationRows, attention, skipReason, representativeId, projectId]);

  const isFiltered = visibleIndividuals.length !== populationRows.length;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={backToReport}>
            <FaArrowLeft /> Back to Assignment Completion
          </BackButton>
          <Title>
            {detail?.businessUnit?.name || 'Business Unit'}
            {detail?.cluster?.name ? ` · ${detail.cluster.name}` : ''}
            {detail?.period?.year ? ` · ${detail.period.year}` : ''}
          </Title>
        </HeaderLeft>
        <HeaderStats>
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <>
              <Tooltip title={columnHelp.eligible}>
                <HeaderStat>
                  <div className="top">
                    <span className="label">Measured</span>
                    <span className="value">{summary.eligibleCount}</span>
                  </div>
                </HeaderStat>
              </Tooltip>
              {/* Skipped people are NOT in the list below — the list is the
                  counted population. This stat explains the difference. */}
              <Tooltip title={skippedHeaderHelp}>
                <HeaderStat>
                  <div className="top">
                    <span className="label">Skipped</span>
                    <span className="value">{summary.skippedCount ?? 0}</span>
                  </div>
                  <span className="sub">Not listed below</span>
                </HeaderStat>
              </Tooltip>
              <Tooltip title={columnHelp.expected}>
                <HeaderStat>
                  <div className="top">
                    <span className="label">Expected</span>
                    <span className="value">
                      {summary.expectedCount === null ? '—' : summary.expectedCount}
                    </span>
                  </div>
                </HeaderStat>
              </Tooltip>
              <Tooltip title={columnHelp.notCompleted}>
                <HeaderStat>
                  <div className="top">
                    <span className="label">Open</span>
                    <span className="value">{summary.notCompletedCount}</span>
                  </div>
                </HeaderStat>
              </Tooltip>
              <Tooltip title={columnHelp.completed}>
                <HeaderStat>
                  <div className="top">
                    <span className="label">Completed</span>
                    <span className="value">{summary.completedCount}</span>
                  </div>
                </HeaderStat>
              </Tooltip>
              <Tooltip title={columnHelp.missing}>
                <HeaderStat>
                  <div className="top">
                    <span className="label">Missing</span>
                    <span className="value">
                      {summary.missingCount === null ? '—' : summary.missingCount}
                    </span>
                  </div>
                </HeaderStat>
              </Tooltip>
              <Tooltip title={columnHelp.completionRate}>
                <HeaderStat>
                  <div className="top">
                    <span className="label">% Completed</span>
                    <span className="value">
                      {summary.completionRate === null ? '—' : `${summary.completionRate}%`}
                    </span>
                  </div>
                </HeaderStat>
              </Tooltip>
            </>
          )}
        </HeaderStats>
      </Header>

      <Content>
        {/* The scrolling element (see the restore effect) — not the window. */}
        <MainSection ref={scrollAreaRef}>
          {isLoading ? (
            <LoadingSpinner />
          ) : error ? (
            <EmptyState
              title="Couldn't Load This Business Unit"
              description="The details couldn't be loaded. Go back to the report and try again."
            />
          ) : individuals.length === 0 ? (
            <EmptyState
              title="No Individuals Found"
              description="Nobody in this business unit is eligible for the selected period and filters."
            />
          ) : (
            <>
              {/* Filters narrow the LIST only — the header keeps describing
                  the whole business unit, so this page never stops restating
                  the number that was clicked on the report. */}
              <FiltersBar>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Show</InputLabel>
                  <Select
                    value={population}
                    label="Show"
                    onChange={(e) => setPopulation(e.target.value)}
                  >
                    {POPULATION_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* ADR-108: only while skipped people can be on screen */}
                {population !== POPULATION.ACTIVE && (
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Skipped because</InputLabel>
                    <Select
                      value={skipReason}
                      label="Skipped because"
                      onChange={(e) => setSkipReason(e.target.value)}
                    >
                      {SKIP_REASON_OPTIONS.map((option) => (
                        <MenuItem key={option.value || 'all'} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <FormControl size="small" sx={{ minWidth: 190 }}>
                  <InputLabel>Attention</InputLabel>
                  <Select
                    value={attention}
                    label="Attention"
                    onChange={(e) => setAttention(e.target.value)}
                  >
                    {ATTENTION_OPTIONS.map((option) => (
                      <MenuItem key={option.value || 'all'} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Representative</InputLabel>
                  <Select
                    value={representativeId}
                    label="Representative"
                    onChange={(e) => setRepresentativeId(e.target.value)}
                  >
                    <MenuItem value=""><em>All Representatives</em></MenuItem>
                    {representativeOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>FT Project</InputLabel>
                  <Select
                    value={projectId}
                    label="FT Project"
                    onChange={(e) => setProjectId(e.target.value)}
                  >
                    <MenuItem value=""><em>All Projects</em></MenuItem>
                    {projectOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

              </FiltersBar>

              <div style={{ marginBottom: '8px' }}>
                <ResultsCount
                  count={visibleIndividuals.length}
                  singular="individual"
                  plural="individuals"
                />
                {isFiltered && (
                  <span style={{ fontSize: '13px', color: '#94A3B8', marginLeft: '8px' }}>
                    of {individuals.length} — the totals above cover the whole business unit
                  </span>
                )}
              </div>

              {visibleIndividuals.length === 0 && (
                <EmptyState
                  title="No Matching Individuals"
                  description="Nobody in this business unit matches the current filters. Try widening them above."
                />
              )}

              {visibleIndividuals.length > 0 && (
              <GridHeader>
                <span>Individual</span>
                <Tooltip title="The full-time allocation that places this individual in this business unit — the same allocation the report counts them under.">
                  <span className="help">FT Project</span>
                </Tooltip>
                <span>Representative</span>
                <span className="num">Expected</span>
                <span className="num">Open</span>
                <span className="num">Completed</span>
                <span className="num">% Completed</span>
              </GridHeader>
              )}

              {visibleIndividuals.map((individual) => {
                const employeeId = individual.employee.id;
                // Skipped rows have no measured work to reveal, so they don't
                // expand — a chevron that opens an empty drawer is a lie.
                const isExpanded = !individual.skipped && expandedIds.has(employeeId);

                return (
                  <React.Fragment key={employeeId}>
                    <IndividualRow
                      $rate={individual.completionRate}
                      $skipped={individual.skipped}
                      onClick={() => !individual.skipped && toggleExpanded(employeeId)}
                    >
                      <div className="person">
                        <span className="chevron">
                          {individual.skipped
                            ? null
                            : isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                        </span>
                        <EmployeeAvatar employee={individual.employee} size={32} />
                        <div className="identity">
                          <div className="name-row">
                            <span
                              className="name"
                              onClick={(e) => handleNavigateToEmployee(individual, e)}
                            >
                              {individual.employee.fullName || '—'}
                            </span>
                            {individual.skipped && (
                              <Tooltip title={skipTagHelp(individual)}>
                                <span className={`skip-tag${individual.skipReason === SKIPPED_REASONS.ENGAGEMENT_MODEL ? ' excluded' : ''}`}>
                                  {getSkippedReasonLabel(individual)}
                                </span>
                              </Tooltip>
                            )}
                          </div>
                          <div className="detail">
                            {individual.employee.position?.label || 'No position'}
                            {individual.employee.grade?.label && ` • ${individual.employee.grade.label}`}
                          </div>
                        </div>
                      </div>

                      {individual.project ? (
                        <Tooltip title={`Allocated ${formatAllocationWindow(individual)}`}>
                          <div className="project">{individual.project.name}</div>
                        </Tooltip>
                      ) : (
                        <div className="project none">—</div>
                      )}

                      <div className="manager">
                        {individual.directManager?.fullName || '—'}
                      </div>

                      {individual.skipped ? (
                        // No expectation is set for them and no work is
                        // counted — dashes, not zeros, which would read as
                        // "expected two, did none".
                        <>
                          <div className="num muted">—</div>
                          <div className="num muted">—</div>
                          <div className="num muted">—</div>
                          <div className="num muted">—</div>
                        </>
                      ) : (
                        <>
                          {/* ADR-096 §6: name the rule behind this number.
                              "Why does she expect 1 and he expect 2?" is asked
                              here, on the page that already shows the position
                              — so it gets answered here rather than sending
                              the reader to the config page. */}
                          {/* Three rules can produce this number now
                              (ADR-097): the person's position, their position
                              TYPE, or the cluster. Name which. */}
                          <Tooltip title={expectationTooltip(individual.expectationSource)}>
                            <div className={`num${individual.expectedCount === null ? ' muted' : ''}`}>
                              {individual.expectedCount === null ? '—' : individual.expectedCount}
                              {expectationTag(individual.expectationSource) && (
                                <span className="ruleTag">
                                  {expectationTag(individual.expectationSource)}
                                </span>
                              )}
                            </div>
                          </Tooltip>
                          <div className={`num${individual.notCompletedCount === 0 ? ' muted' : ''}`}>
                            {individual.notCompletedCount}
                          </div>
                          <Tooltip
                            title={individual.surplusCount > 0
                              ? `${individual.surplusCount} more ${individual.surplusCount === 1 ? 'assignment' : 'assignments'} that count toward no target — already enough of that review type, or no review type set yet`
                              : ''}
                          >
                            <div className={`num${individual.completedCount === 0 ? ' muted' : ''}`}>
                              {individual.completedCount}
                              {individual.surplusCount > 0 && (
                                <span className="surplus">+{individual.surplusCount}</span>
                              )}
                            </div>
                          </Tooltip>
                          <div className="num rate">
                            {individual.completionRate === null ? '—' : `${individual.completionRate}%`}
                          </div>
                        </>
                      )}
                    </IndividualRow>

                    {isExpanded && (
                      <AssignmentList>
                        {individual.assignments.length === 0 ? (
                          // The Missing count made concrete — the most
                          // actionable state on the page.
                          <div className="empty">
                            No assignments created for {detail?.period?.year} — {individual.missingCount} expected.
                          </div>
                        ) : (
                          individual.assignments.map((assignment) => {
                            const statusLabel = getStatusText(assignment.status);
                            const description = getStatusDescription(assignment.status);
                            // The shared helper has no copy for 'published';
                            // show no tooltip rather than "Unknown status".
                            const hasDescription = description && description !== 'Unknown status';

                            const chip = (
                              <span
                                className="status"
                                style={{
                                  backgroundColor: getStatusColor(assignment.status),
                                  color: getStatusTextColor(assignment.status),
                                }}
                              >
                                {statusLabel}
                              </span>
                            );

                            const target = assignmentTarget(assignment);

                            return (
                              <AssignmentLine key={assignment.id}>
                                {/* No tooltip: the link styling already says
                                    it is clickable, and "Open this feedback
                                    session" only restated that. */}
                                {target ? (
                                  <span
                                    className="source link"
                                    onClick={() => openAssignmentSource(target)}
                                  >
                                    {assignment.source?.name}
                                  </span>
                                ) : (
                                  <span className="source">{assignment.source?.name}</span>
                                )}
                                {assignment.reviewType ? (
                                  <span
                                    className="type"
                                    style={{
                                      backgroundColor: getReviewTypeColor(assignment.reviewType),
                                      color: getReviewTypeTextColor(assignment.reviewType),
                                      border: `1px solid ${getReviewTypeBorderColor(assignment.reviewType)}`,
                                    }}
                                  >
                                    {getReviewTypeText(assignment.reviewType)}
                                  </span>
                                ) : (
                                  <span className="type none">No reviewers yet</span>
                                )}
                                {/* ONE badge per slot (ADR-014 §10c): frozen
                                    work shows "Left Incomplete" INSTEAD of its
                                    actionable status — the status it stopped
                                    at moves into the badge's tooltip. */}
                                {assignment.frozen ? (
                                  <AssignmentFrozenBadge
                                    status={assignment.status}
                                    sx={{ fontSize: '12px', height: 22, justifySelf: 'start' }}
                                  />
                                ) : hasDescription ? (
                                  <Tooltip title={description}>{chip}</Tooltip>
                                ) : chip}
                              </AssignmentLine>
                            );
                          })
                        )}
                      </AssignmentList>
                    )}
                  </React.Fragment>
                );
              })}
            </>
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default ServiceUnitDetail;
